/**
 * Type guards for reading a gateway response. Every one of them refuses rather
 * than coerces: a response carries prices among other things, and a reader that
 * quietly turned a malformed figure into a number would publish a price nobody
 * authored.
 */
export type JsonRecord = Record<string, unknown>;

/** The one error every reader throws, naming the field (or wire path) it refused. */
export const invalid = (label: string): never => {
  throw new Error(`Invalid ${label}`);
};

export const readRecord = (value: unknown, label: string): JsonRecord => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid(label);
  return value as JsonRecord;
};

export const readString = (value: unknown, label: string): string => {
  if (typeof value !== 'string') return invalid(label);
  return value;
};

export const readNonEmptyString = (value: unknown, label: string): string => {
  const text = readString(value, label);
  if (text.length === 0) return invalid(label);
  return text;
};

export const readEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[],
  label: string,
): T => {
  const text = readString(value, label);
  if (!allowed.includes(text as T)) throw new Error(`Invalid ${label}`);
  return text as T;
};

export const readOptionalString = (value: unknown, label: string): string | undefined =>
  value === undefined || value === null ? undefined : readString(value, label);

export const readNullableString = (value: unknown, label: string): string | null =>
  value === null || value === undefined ? null : readString(value, label);

export const readNumber = (value: unknown, label: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`Invalid ${label}`);
  return value;
};

/** Customer-facing USD values may be zero, but never negative or non-finite. */
export const readMoney = (value: unknown, label: string): number => {
  const amount = readNumber(value, label);
  if (amount < 0) throw new Error(`Invalid ${label}`);
  return amount;
};

/** A whole number that may be negative, such as a Reddit score. */
export const readInteger = (value: unknown, label: string): number => {
  const result = readNumber(value, label);
  if (!Number.isInteger(result)) return invalid(label);
  return result;
};

/** A whole count at or above `minimum` (zero unless a caller needs one). */
export const readCount = (value: unknown, label: string, minimum = 0): number => {
  const count = readInteger(value, label);
  if (count < minimum) return invalid(label);
  return count;
};

/** A non-negative number inside a range, such as a percentage or a relevance
 *  score. The lower bound is exclusive when given; the upper is inclusive. */
export const readBoundedNumber = (
  value: unknown,
  label: string,
  minimumExclusive: number | undefined,
  maximumInclusive: number,
): number => {
  const parsed = readMoney(value, label);
  if ((minimumExclusive !== undefined && parsed <= minimumExclusive) || parsed > maximumInclusive) {
    return invalid(label);
  }
  return parsed;
};

/** One-time credentials must be present before they are exposed to a caller. */
export const readSecret = (value: unknown, label: string): string => {
  const secret = readString(value, label);
  if (secret.trim() === '') throw new Error(`Invalid ${label}`);
  return secret;
};

const isOAuthLoopbackHost = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '[::1]' || /^127(?:\.\d{1,3}){3}$/.test(hostname);

/** Hosted handoffs require HTTPS. */
export const readHttpsUrl = (value: unknown, label: string): string => {
  const raw = readString(value, label);
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return invalid(label);
  }
  if (url.protocol !== 'https:') return invalid(label);
  return raw;
};

/** OAuth browser redirects mirror the gateway's DCR policy: HTTPS for hosted
 * clients and HTTP only for loopback clients. Credentials and fragments are
 * never valid redirect targets. */
export const readOAuthRedirectUrl = (value: unknown, label: string): string => {
  const raw = readString(value, label);
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new Error(`Invalid ${label}`);
  }
  const loopback = isOAuthLoopbackHost(url.hostname);
  if ((loopback ? url.protocol !== 'http:' : url.protocol !== 'https:')
    || url.username !== ''
    || url.password !== ''
    || url.hash !== '') {
    throw new Error(`Invalid ${label}`);
  }
  return raw;
};

export const readBoolean = (value: unknown, label: string): boolean => {
  if (typeof value !== 'boolean') throw new Error(`Invalid ${label}`);
  return value;
};

export const readArray = (value: unknown, label: string): unknown[] => {
  if (!Array.isArray(value)) throw new Error(`Invalid ${label}`);
  return value;
};
